<template>
  <UiModal
    v-model="userSelectModalModel"
    :title="displayTitle"
    size="large"
    :show-footer="true"
    :no-dim="noDim"
    :check-text-changes="checkTextChanges"
    :body-scroll-lock="!noDim"
    :teleport-to-body="true"
    :z-index="10060"
    class="user-select-modal-wrapper"
  >
    <!-- 검색 필터 추가 -->
    <div v-if="mode === 'one'" class="user-select-filters">
      <div class="filter-row">
        <UiInput :placeholder="t('common.userSelect.namePlaceholder')" v-model="userNameFilter" class="filter-input" @keyup.enter="searchUser" />
        <UiSelect
          v-if="mode === 'one' && filterType === 'all'"
          :placeholder="t('common.userSelect.positionPlaceholder')"
          v-model="userPositionFilter"
          :options="positionOptions"
          class="filter-select"
        />
        <UiSelect
          v-if="mode === 'one' && filterType === 'all'"
          :placeholder="t('common.userSelect.rankPlaceholder')"
          v-model="userRankFilter"
          :options="rankOptions"
          class="filter-select"
        />
        <UiSwitch v-model="showUserId" :label="t('common.userSelect.showUserId')" class="filter-id-search-switch" />
        <UiButton type="primary" class="filter-search-btn" @click="searchUser">
          <i class="icon icon-md icon-search icon-white"></i>
          {{ t('common.userSelect.search') }}
        </UiButton>
      </div>
    </div>
    <!-- 조건에 따라 렌더링 분기 -->
    <DepartmentUserSelector v-if="mode === 'one'" ref="DepartmentUserSelectorRef" :find-year="findYear" :eval-cycle-cd="evalCycleCd" />
    <OrganizationUserSelector
      v-else-if="mode === 'multi'"
      ref="OrganizationUserSelectorRef"
      :find-year="findYear"
      :eval-cycle-cd="evalCycleCd"
      :exclude-user-ids="excludeUserIds"
    />

    <!-- 하단 버튼 -->
    <template #footerActions>
      <UiButton variant="secondary" @click="handleCancel">{{ t('common.action.cancel') }}</UiButton>
      <UiButton variant="primary" @click="handleConfirm">{{ t('common.confirm') }}</UiButton>
    </template>
  </UiModal>
</template>

<script setup>
import { ref, watch, onMounted, unref, nextTick, computed } from 'vue';
import DepartmentUserSelector from '@/components/user/DepartmentUserSelector.vue';
import OrganizationUserSelector from '@/components/user/OrganizationUserSelector.vue';
import { useHandoverStore } from '~/composables/useHandoverStore.ts';
import { useUserSelectorStore } from '~/composables/useUserSelectorStore.js';
import { useUserSelectorApi } from '~/composables/useUserSelectorApi.js';
import { showAlert } from '~/composables/useConfirmDialog';

const { userNameFilter, userPositionFilter, userRankFilter } = useHandoverStore();
const { t } = useI18n();

const LEGACY_DEFAULT_TITLES = ['담당자 선택'];
const { selectedUsers, selectedUserObject, users, selectedDept, orgEmployeesData, selectedOrgId, employeeName, selectedFromDept, selectedFromSelected, isSearch, showUserId } =
  useUserSelectorStore();
const { fetchUserList, fetchUserInsaList } = useUserSelectorApi();

const props = defineProps({
  modelValue: Boolean,
  mode: {
    type: String,
    default: 'one', // 'multi'
  },
  filterType: {
    type: String,
    default: 'name', // 'all'
  },
  callbackComponentName: {
    type: String,
    default: '',
  },
  // 초기 선택된 사용자 목록 (multi 모드에서 사용)
  initialSelectedUsers: {
    type: Array,
    default: () => [],
  },
  // 선택 대상에서 제외할 사용자 ID 목록 (예: 배포 대상에서 본인 제외)
  excludeUserIds: {
    type: Array,
    default: () => [],
  },
  // dim 숨김 여부 (다른 모달 위에 열릴 때 사용)
  noDim: {
    type: Boolean,
    default: false,
  },
  findYear: {
    type: String,
    default: '',
  },
  evalCycleCd: {
    type: String,
    default: '',
  },
  /** false: 닫을 때 입력값 변경 확인 생략 (선택 팝업 등) */
  checkTextChanges: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: '',
  },
});

const displayTitle = computed(() => {
  if (!props.title || LEGACY_DEFAULT_TITLES.includes(props.title)) {
    return t('common.userSelect.title');
  }
  return props.title;
});

const emits = defineEmits(['update:modelValue', 'confirm']);
const positionOptions = ref([]);
const rankOptions = ref([]);

const userSelectModalModel = computed({
  get: () => props.modelValue,
  set: value => {
    emits('update:modelValue', value);
  },
});

onMounted(async () => {
  // 직급, 직위 설정
  const commonStore = useCommonStore();

  const posList = await commonStore.getCodePosList();
  positionOptions.value = [{ value: '', label: t('common.action.all') }, ...posList];

  const jobList = await commonStore.getCodeJobList();
  rankOptions.value = [{ value: '', label: t('common.action.all') }, ...jobList];
});

// 부모 >> 자식 값 전달
watch(
  () => props.modelValue,
  v => {
    if (v) {
      // 팝업 열릴 때 검색조건 초기화
      userNameFilter.value = null;
      userRankFilter.value = null;
      userPositionFilter.value = null;
      showUserId.value = false;

      // DepartmentUserSelector: 전역 store의 사용자 목록·부서 선택이 모달 닫힌 뒤에도 남지 않도록 매번 비움
      users.value = [];
      selectedDept.value = null;
      selectedUserObject.value = null;

      // OrganizationUserSelector: 소속 직원/검색/선택 하이라이트가 남지 않도록 매번 비움
      orgEmployeesData.value = [];
      selectedOrgId.value = '';
      employeeName.value = '';
      isSearch.value = false;
      selectedFromDept.value = new Set();
      selectedFromSelected.value = new Set();

      // multi 모드일 때 초기 선택 사용자로 초기화 (제외 대상은 걸러냄)
      if (props.mode === 'multi' && props.initialSelectedUsers.length > 0) {
        selectedUsers.value = props.initialSelectedUsers.filter(u => !props.excludeUserIds.includes(u.userId));
      } else {
        selectedUsers.value = [];
      }
    }
  },
);

const DepartmentUserSelectorRef = ref(null);
const OrganizationUserSelectorRef = ref(null);

// 담당자 선택 팝업 담당자 검색
const searchUser = async () => {
  try {
    const shouldUseInsaApi = !!props.findYear && !!props.evalCycleCd;
    const result = shouldUseInsaApi
      ? await fetchUserInsaList({
          findYear: props.findYear,
          deptId: selectedDept.value || '',
          userNm: userNameFilter.value || '',
          evalCycleCd: props.evalCycleCd,
          userIdYn: showUserId.value ? 'Y' : 'N',
        })
      : await fetchUserList({
          compId: 'isp',
          userNm: userNameFilter.value,
          findYear: useCommonStore().findYear || '2024',
          findPosId: userPositionFilter.value,
          findJikgubId: userRankFilter.value,
        });

    if (DepartmentUserSelectorRef.value) {
      DepartmentUserSelectorRef.value.selectedDept = null;
      DepartmentUserSelectorRef.value.users = result.list;
    }
  } catch (err) {
    console.error('담당자 검색 실패', err);
  }
};

// 확인 버튼 클릭
const handleConfirm = async () => {
  await nextTick();
  let result;
  if (props.mode === 'one') {
    result = selectedUserObject.value;
    if (!result && DepartmentUserSelectorRef.value) {
      result = unref(DepartmentUserSelectorRef.value.selectedUserObject);
    }
    if (result && typeof result === 'object') {
      result = { ...result };
    }
  } else {
    // 제외 대상(예: 본인)이 어떤 경로로든 섞이지 않도록 확정 시점에 한 번 더 걸러낸다
    result = selectedUsers.value.filter(u => !props.excludeUserIds.includes(u.userId));
  }

  if (!result || (Array.isArray(result) && result.length === 0)) {
    showAlert(t('common.notice'), t('common.userSelect.selectUserRequired'));
    return;
  }

  emits('confirm', result, props.callbackComponentName);
  userSelectModalModel.value = false;
};

// 취소 버튼 클릭
const handleCancel = () => {
  userSelectModalModel.value = false;
};
</script>

<style scoped lang="scss">
.user-select-filters {
  .filter-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .filter-input {
    flex: 1 1 auto;
    min-width: 0;
  }

  .filter-select {
    flex: 0 0 100px;
    width: 100px;
    min-width: 100px;
  }

  .filter-id-search-switch {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .filter-search-btn {
    flex: 0 0 auto;
  }
}
</style>

